import React from 'react';
import { Feature, Overlay } from 'ol';
import { toLonLat } from 'ol/proj';
import { getCenter } from 'ol/extent';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { useLocation } from 'react-router-dom';
import Transform from 'ol-ext/interaction/Transform';

import {
  MAP_OBJECT_AREA,
  MAP_OBJECT_IMAGE,
  MAP_OBJECT_ZONE
} from '../constants/map';
import {
  AreaEntity,
  TaskEntity,
  ZoneEntity
} from '../types/game-document/entities';
import { MapImage } from '../types/game-document';
import { MapContext } from '../components/map/map-context';
import { useGameTaskPosition } from './use-game-task-position';
import { GameDocumentContext } from '../contexts/game-document';
import { GetResourceValue } from '../utils/game-document/resources';
import { mapContentHighlight } from '../utils/map/map-content-highlight';
import { UpdateGameDocState, UpdateTaskAsync } from '../utils/game-document';
import { MapIllustrationContext } from '../components/map/map-illustration-context';

export const useGameTask = ({
  tasks,
  zones,
  areas,
  mapType,
  mapImages,
  showTaskTitle,
  selectAllAssets,
  updateOverlayImage,
  onTaskPositionUpdated
}: {
  mapType: 'openStreetMap' | 'satelliteMap' | 'illustrated' | 'googleMaps';
  tasks: TaskEntity[];
  zones: ZoneEntity[];
  areas: AreaEntity[];
  mapImages: MapImage[];
  showTaskTitle: boolean;
  selectAllAssets: string;
  updateOverlayImage: (
    id: string,
    newCenter: [number, number],
    newExtent: [number, number, number, number],
    newScale: [number, number] | undefined
  ) => void;
  onTaskPositionUpdated?: (taskId: string, lat: number, lon: number) => void;
}) => {
  const location = useLocation();
  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);
  const [gameDocument, setGameDocument] = React.useContext(GameDocumentContext);
  const {
    taskPopupId,
    addTaskOverlay,
    removeTaskOverlay,
    updateTaskOverlayPosition
  } = useGameTaskPosition({ mapType, showTaskTitle });

  const mapContext = React.useMemo(
    () => (mapType === 'illustrated' ? mapIllustration : map),
    [mapType, map, mapIllustration]
  );

  const removeUnusedTaskOverlay = (currentTaskIds: string[]) => {
    const ids: string[] = [];

    ids.forEach((id) => {
      if (!currentTaskIds.includes(id)) {
        removeTaskOverlay(id);
      }
    });
  };

  const populateTasks = React.useCallback(() => {
    if (!tasks || !Array.isArray(tasks)) return;

    removeUnusedTaskOverlay(tasks.map((task) => task.id));
    tasks.forEach((task) => {
      if (task.boundary?.geometry?.coordinates) {
        const imgUrl = GetResourceValue(
          gameDocument.gameDocument!,
          task.imageResId || ''
        );

        // Fallback to default designer icon if resource not found
        const resolvedImgUrl =
          imgUrl ||
          GetResourceValue(
            gameDocument.gameDocument!,
            gameDocument.gameDocument!.settings?.designer
              ?.defaultTaskAvailableIconResId || ''
          );

        const title = GetResourceValue(
          gameDocument.gameDocument!,
          task.titleResId || ''
        );

        addTaskOverlay(
          task.id,
          title,
          resolvedImgUrl,
          toLonLat(task.boundary.geometry.coordinates),
          false,
          task.isVisible
        );
      }
    });
  }, [tasks, mapContext, gameDocument.gameDocument, addTaskOverlay]);

  React.useEffect(() => {
    populateTasks();
  }, [populateTasks, mapType]);

  React.useEffect(() => {
    const taskContainer = document.querySelectorAll('[id^="taskContainer-"]');
    taskContainer.forEach((task) => {
      task.classList[
        ['ALL', 'TASK'].includes(selectAllAssets) ? 'add' : 'remove'
      ]('selected');
    });
  }, [mapType, map, mapIllustration, selectAllAssets]);

  // drag and drop task location
  React.useEffect(() => {
    const startPixels = { x: 0, y: 0 };
    const startCoords = { x: 0, y: 0 };

    const overlays: Overlay[] = [];
    const taskIds: string[] = [];
    const updatedPositions: number[][] = [];
    const updatedZoneFeature: { id: string; coord: number[][][][] }[] = [];
    const updatedAreaFeature: { id: string; coord: number[][][][] }[] = [];
    const updatedImageFeature: {
      id: string;
      newCenter: [number, number] | null;
      newExtent: [number, number, number, number] | null;
    }[] = [];

    const handleMouseDown = (event: MouseEvent) => {
      // left-click only for dragging task position
      if (event.button !== 0) return;

      const overlayElement = event.target as HTMLElement;
      let taskContainer = overlayElement;
      while (
        taskContainer.parentElement &&
        !taskContainer.id.startsWith('taskContainer-')
      ) {
        taskContainer = taskContainer.parentElement;
      }

      const id = taskContainer.id.replace('taskContainer-', '');
      if (!id) return;

      const transform = mapContext.get('transformInteraction') as
        | Transform
        | undefined;
      const transformImage = mapContext.get('transformInteractionImage') as
        | Transform
        | undefined;
      if (transform) transform.select(undefined, false);
      if (transformImage) transformImage.select(undefined, false);

      mapContentHighlight('task', id);

      if (['ALL', 'TASK'].includes(selectAllAssets)) {
        tasks.forEach((task) => {
          taskIds.push(task.id);
        });
      } else {
        taskIds.push(id);
      }

      taskIds.forEach((id) => {
        const ov = mapContext.getOverlayById(id);
        ov && overlays.push(ov);
      });

      if (
        overlays.length > 0 &&
        overlays.some((ov) => ov.getElement()?.contains(event.target as Node))
      ) {
        startPixels.x = event.clientX;
        startPixels.y = event.clientY;

        overlays.forEach((ov) => {
          ov.getElement()
            ?.querySelector('[class^="task-center-dot"]')
            ?.classList.remove('none');
        });

        const coord = mapContext.getCoordinateFromPixel([
          startPixels.x,
          startPixels.y
        ]);
        startCoords.x = coord[0];
        startCoords.y = coord[1];

        taskIds.forEach((id, index) => {
          const ov = overlays[index];
          if (ov) {
            const currentCoordinate = ov.getPosition();

            if (currentCoordinate) {
              updatedPositions[index] = currentCoordinate;
            }
          }
        });

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (overlays.length > 0 && taskIds.length > 0) {
        const currentPixel = [event.clientX, event.clientY];
        const currentCoord = mapContext.getCoordinateFromPixel(currentPixel);

        const deltaX = currentCoord[0] - startCoords.x;
        const deltaY = currentCoord[1] - startCoords.y;

        // Get map extent for constraint checking
        const mapExtent = mapContext.getView().getProjection().getExtent();

        taskIds.forEach((id, index) => {
          const ov = overlays[index];
          if (ov) {
            const currentCoordinate = ov.getPosition();

            if (currentCoordinate) {
              // Calculate new position
              let newPosition = [
                currentCoordinate[0] + deltaX,
                currentCoordinate[1] + deltaY
              ];

              // Constrain to map boundaries
              // X-axis constraints
              if (newPosition[0] < mapExtent[0]) {
                newPosition[0] = mapExtent[0];
              }
              if (newPosition[0] > mapExtent[2]) {
                newPosition[0] = mapExtent[2];
              }

              // Y-axis constraints
              if (newPosition[1] < mapExtent[1]) {
                newPosition[1] = mapExtent[1];
              }
              if (newPosition[1] > mapExtent[3]) {
                newPosition[1] = mapExtent[3];
              }

              // Apply constrained position
              ov.setPosition(newPosition);
              updateTaskOverlayPosition(id, toLonLat(newPosition));
              updatedPositions[index] = newPosition;
            }
          }
        });

        startCoords.x = currentCoord[0];
        startCoords.y = currentCoord[1];

        if (selectAllAssets === 'ALL') {
          mapContext.getLayers().forEach((layer) => {
            if (layer instanceof VectorLayer) {
              const source = layer.getSource();
              if (source instanceof VectorSource) {
                const features = source.getFeatures();

                if (Array.isArray(features)) {
                  features.forEach((f, idx) => {
                    const geometry = f.getGeometry();
                    const objectType = f.get('objectType');

                    // Apply constraints to vector features
                    geometry.translate(deltaX, deltaY);

                    // Constrain to map boundaries
                    const featureExtent = geometry.getExtent();
                    let isModified = false;

                    // X-axis constraints
                    if (featureExtent[0] < mapExtent[0]) {
                      geometry.translate(mapExtent[0] - featureExtent[0], 0);
                      isModified = true;
                    }
                    if (featureExtent[2] > mapExtent[2]) {
                      geometry.translate(mapExtent[2] - featureExtent[2], 0);
                      isModified = true;
                    }

                    // Y-axis constraints
                    if (featureExtent[1] < mapExtent[1]) {
                      geometry.translate(0, mapExtent[1] - featureExtent[1]);
                      isModified = true;
                    }
                    if (featureExtent[3] > mapExtent[3]) {
                      geometry.translate(0, mapExtent[3] - featureExtent[3]);
                      isModified = true;
                    }

                    // Store updated coordinates
                    switch (objectType) {
                      case MAP_OBJECT_ZONE:
                        updatedZoneFeature[idx] = {
                          id: f.getId(),
                          coord: geometry.getCoordinates()
                        };
                        break;
                      case MAP_OBJECT_AREA:
                        updatedAreaFeature[idx] = {
                          id: f.getId(),
                          coord: geometry.getCoordinates()
                        };
                        break;
                      case MAP_OBJECT_IMAGE:
                        const imageExtent = geometry.getExtent() as [
                          number,
                          number,
                          number,
                          number
                        ];
                        const imageCenter = getCenter(imageExtent) as [
                          number,
                          number
                        ];
                        updatedImageFeature[idx] = {
                          id: f.getId(),
                          newCenter: imageCenter,
                          newExtent: imageExtent
                        };
                        break;
                    }
                  });
                }
              }
            }
          });
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      overlays.forEach((ov) => {
        ov.getElement()
          ?.querySelector('[class^="task-center-dot"]')
          ?.classList.add('none');
      });

      if (taskIds.length > 0 && updatedPositions.length > 0) {
        taskIds.forEach(async (id, index) => {
          const selectedTask = tasks.find((task) => task.id === id);
          if (
            gameDocument.gameDocument &&
            selectedTask &&
            selectedTask.boundary
          ) {
            selectedTask.boundary.geometry.coordinates =
              updatedPositions[index];

            UpdateTaskAsync(gameDocument.gameDocument, id, selectedTask).then(
              (response) => {
                setGameDocument((prev) => UpdateGameDocState(prev, response));
                const [lon, lat] = toLonLat(updatedPositions[index]);
                onTaskPositionUpdated?.(id, lat, lon);
              }
            );
          }
        });
      }

      zones.forEach((zone) => {
        if (zone.boundary) {
          const id = `zone-${zone.boundary.id}`;
          const getUpdated = updatedZoneFeature
            .filter((z) => z.id)
            .find((z) => z.id === id);
          if (getUpdated)
            zone.boundary.geometry.coordinates = getUpdated?.coord;
        }
      });

      areas.forEach((area) => {
        if (area.boundary) {
          const id = `area-${area.boundary.id}`;
          const getUpdated = updatedAreaFeature
            .filter((a) => a.id)
            .find((a) => a.id === id);
          if (getUpdated)
            area.boundary.geometry.coordinates = getUpdated?.coord;
        }
      });

      updatedImageFeature.length > 0 &&
        updatedImageFeature.forEach((image) => {
          const { id, newCenter, newExtent } = image;
          if (newCenter && newExtent) {
            updateOverlayImage(id, newCenter, newExtent, undefined);
          }
        });
    };

    location.pathname.includes('map') &&
      document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [
    tasks,
    zones,
    mapImages,
    mapContext,
    selectAllAssets,
    location.pathname,
    gameDocument.gameDocument,
    updateTaskOverlayPosition
  ]);

  return {
    taskPopupId,
    addTaskOverlay,
    removeTaskOverlay,
    updateTaskOverlayPosition
  };
};
